import type { Vector3 } from "three";

import type { Direction, Piece, Side } from "src/shared/board";
import type { StructureKind } from "src/shared/industry";

/**
 * What is in the player's hand while they drag.
 *
 * **Three bodies, one gesture.** A piece already on the board is *held* — its
 * own view leaves its square, so nothing is drawn twice — while a forge and a
 * building have no body yet and get one made for the length of the drag.
 */
export type CarryGhost =
  | { kind: "held"; square: number }
  /** `facing` is a pawn's, and absent means the army's own. */
  | { kind: "piece"; piece: Piece; facing?: Direction }
  | { kind: "structure"; structure: StructureKind; owner: Side };

/** A carry as the renderer takes it: the body, and the squares it snaps to. */
export interface CarrySpec {
  ghost: CarryGhost;
  targets: readonly number[];
}

/** No square: the body follows the pointer. */
export const CARRY_FREE = -1;
/** How far off the floor a carried body floats, free and over a square. */
export const CARRY_LIFT = 0.3;
export const CARRY_SNAP_LIFT = 0.12;
/**
 * The height the pointer holds a body at. About a piece's middle, so the body
 * is under the cursor rather than its base — at a 3/4 pitch those are most of
 * a square apart.
 */
export const CARRY_GRIP = 0.45;

/** How fast a jump is taken up, per second. Exponential, so frame-rate free. */
const CATCH_UP = 22;
const STILL = 1e-5;

/**
 * Where a carried body is drawn.
 *
 * **Glued to the pointer, and eased only across a jump.** Following is 1:1 for
 * the pan's reason — a body that trails the hand is not in it. What would jump
 * is the body entering or leaving a square, so that one step is kept as an
 * `offset` and decayed, which leaves the position continuous and the follow
 * exact again a moment later.
 */
export class CarryMotion {
  private desired: Vector3;
  private offset: Vector3;
  private at = CARRY_FREE - 1;

  /** `position` is the body's own vector, written in place. */
  constructor(private position: Vector3) {
    this.desired = position.clone();
    this.offset = position.clone().set(0, 0, 0);
  }

  /**
   * Aim at a point. `square` is the one it is snapped to, or `CARRY_FREE`.
   *
   * Free to free keeps the offset — that is the glue. Anything else is a jump.
   */
  aim(desired: Vector3, square: number): void {
    const glued = square === CARRY_FREE && this.at === CARRY_FREE;
    if (!glued && square !== this.at) this.offset.copy(this.position).sub(desired);
    this.at = square;
    this.desired.copy(desired);
    this.position.copy(desired).add(this.offset);
  }

  /** Take up some of the jump. True while there is any left. */
  update(dt: number): boolean {
    if (this.offset.lengthSq() < STILL) return false;
    this.offset.multiplyScalar(Math.exp(-CATCH_UP * dt));
    if (this.offset.lengthSq() < STILL) this.offset.set(0, 0, 0);
    this.position.copy(this.desired).add(this.offset);
    return true;
  }
}
